import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  Plus, Save, Eye, Trash2, Settings, Layout, Type, Image, Video, MessageSquare, Users,
  BarChart3, Star, FileText, Mail, Phone, MapPin, ChevronUp, ChevronDown, Copy, Sparkles,
  Wand2, Globe, Search, Link, Check, Loader2, ArrowRight, Columns, Grid3X3, GripVertical,
  X, Rocket, HelpCircle, Minus, Undo, Redo, Monitor, Smartphone, Tablet, ChevronRight,
  Play, Code, PanelLeftClose, History, Clock, Send, CheckCircle, AlertCircle, RefreshCw,
  Maximize2, ExternalLink, RotateCcw
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

// Block Types
const BLOCK_TYPES = [
  { id: 'hero', name: 'Hero Banner', icon: Rocket, category: 'sections', desc: 'Section hero avec titre et CTA' },
  { id: 'hero-video', name: 'Hero Vidéo', icon: Play, category: 'sections', desc: 'Hero avec fond vidéo', pro: true },
  { id: 'text', name: 'Texte', icon: Type, category: 'contenu', desc: 'Bloc de texte riche' },
  { id: 'image', name: 'Image', icon: Image, category: 'media', desc: 'Image avec légende' },
  { id: 'image-text', name: 'Image + Texte', icon: Columns, category: 'sections', desc: 'Image avec texte' },
  { id: 'video', name: 'Vidéo', icon: Video, category: 'media', desc: 'Vidéo YouTube ou MP4' },
  { id: 'gallery', name: 'Galerie', icon: Grid3X3, category: 'media', desc: 'Galerie d\'images' },
  { id: 'services', name: 'Services', icon: Grid3X3, category: 'sections', desc: 'Grille de services' },
  { id: 'features', name: 'Fonctionnalités', icon: Star, category: 'sections', desc: 'Liste avec icônes' },
  { id: 'testimonials', name: 'Témoignages', icon: MessageSquare, category: 'social', desc: 'Avis clients' },
  { id: 'team', name: 'Équipe', icon: Users, category: 'sections', desc: 'Présentation équipe' },
  { id: 'stats', name: 'Statistiques', icon: BarChart3, category: 'sections', desc: 'Chiffres animés' },
  { id: 'faq', name: 'FAQ', icon: HelpCircle, category: 'contenu', desc: 'Questions fréquentes' },
  { id: 'cta', name: 'Call to Action', icon: ArrowRight, category: 'cta', desc: 'Bannière CTA' },
  { id: 'contact-form', name: 'Formulaire', icon: Mail, category: 'forms', desc: 'Formulaire contact' },
  { id: 'map', name: 'Carte', icon: MapPin, category: 'media', desc: 'Google Maps' },
  { id: 'spacer', name: 'Espacement', icon: Minus, category: 'layout', desc: 'Espace vertical' },
  { id: 'case-studies', name: 'Réalisations', icon: FileText, category: 'sections', desc: 'Études de cas' },
  { id: 'html', name: 'HTML Custom', icon: Code, category: 'avance', desc: 'Code personnalisé', pro: true }
];

const CATEGORIES = [
  { id: 'all', name: 'Tous' },
  { id: 'sections', name: 'Sections' },
  { id: 'contenu', name: 'Contenu' },
  { id: 'media', name: 'Média' },
  { id: 'forms', name: 'Formulaires' },
  { id: 'social', name: 'Social' },
  { id: 'cta', name: 'CTA' },
  { id: 'layout', name: 'Layout' }
];

// Live Preview Component
const LivePreview = ({ page, device }) => {
  const iframeRef = useRef(null);

  const getPreviewWidth = () => {
    switch (device) {
      case 'mobile': return '375px';
      case 'tablet': return '768px';
      default: return '100%';
    }
  };

  // Generate preview HTML
  const generatePreviewHTML = () => {
    const blocks = page?.blocks || [];
    
    let blocksHTML = blocks.map(block => {
      switch (block.type) {
        case 'hero':
          return `
            <section class="min-h-[50vh] bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-8 text-center">
              <div>
                <h1 class="text-4xl font-bold text-white mb-4">${block.content?.title || 'Titre principal'}</h1>
                <p class="text-xl text-slate-300 mb-6">${block.content?.subtitle || 'Sous-titre de votre section hero'}</p>
                ${block.content?.cta_text ? `<button class="bg-cyan-500 text-white px-6 py-3 rounded-full">${block.content.cta_text}</button>` : ''}
              </div>
            </section>
          `;
        case 'text':
          return `
            <section class="py-12 px-8">
              <div class="max-w-3xl mx-auto">
                <p class="text-slate-600 text-lg leading-relaxed">${block.content?.text || 'Votre texte ici...'}</p>
              </div>
            </section>
          `;
        case 'image':
          return `
            <section class="py-8 px-8">
              <div class="max-w-4xl mx-auto">
                <img src="${block.content?.url || 'https://via.placeholder.com/800x400'}" alt="${block.content?.alt || ''}" class="w-full rounded-xl shadow-lg" />
                ${block.content?.caption ? `<p class="text-center text-slate-500 mt-4">${block.content.caption}</p>` : ''}
              </div>
            </section>
          `;
        case 'services':
          return `
            <section class="py-16 px-8 bg-slate-50">
              <div class="max-w-6xl mx-auto">
                <h2 class="text-3xl font-bold text-center mb-4">${block.content?.title || 'Nos Services'}</h2>
                <p class="text-slate-600 text-center mb-12">${block.content?.subtitle || ''}</p>
                <div class="grid grid-cols-${block.content?.columns || 3} gap-6">
                  ${[1,2,3].map(i => `
                    <div class="bg-white p-6 rounded-xl shadow-md">
                      <div class="w-12 h-12 bg-cyan-500 rounded-lg mb-4"></div>
                      <h3 class="font-semibold mb-2">Service ${i}</h3>
                      <p class="text-slate-600 text-sm">Description du service</p>
                    </div>
                  `).join('')}
                </div>
              </div>
            </section>
          `;
        case 'stats':
          const statsLines = (block.content?.stats_raw || '150+|Projets\n98%|Satisfaits').split('\n');
          return `
            <section class="py-16 px-8 bg-slate-900">
              <div class="max-w-4xl mx-auto grid grid-cols-${statsLines.length} gap-8 text-center">
                ${statsLines.map(line => {
                  const [value, label] = line.split('|');
                  return `
                    <div>
                      <div class="text-4xl font-bold text-cyan-400 mb-2">${value}</div>
                      <div class="text-slate-400">${label}</div>
                    </div>
                  `;
                }).join('')}
              </div>
            </section>
          `;
        case 'cta':
          return `
            <section class="py-16 px-8 bg-gradient-to-r from-cyan-500 to-blue-600 text-center text-white">
              <h2 class="text-3xl font-bold mb-4">${block.content?.title || 'Prêt à démarrer ?'}</h2>
              <p class="text-white/80 mb-8">${block.content?.description || ''}</p>
              <button class="bg-white text-cyan-600 px-8 py-3 rounded-full font-semibold">
                ${block.content?.button_text || 'Contactez-nous'}
              </button>
            </section>
          `;
        case 'spacer':
          return `<div style="height: ${block.content?.height || 60}px"></div>`;
        default:
          return `
            <section class="py-8 px-8 bg-slate-100 text-center">
              <p class="text-slate-500">Bloc: ${block.type}</p>
            </section>
          `;
      }
    }).join('');

    if (!blocksHTML) {
      blocksHTML = `
        <div class="min-h-[50vh] flex items-center justify-center bg-slate-50">
          <div class="text-center">
            <div class="text-6xl mb-4">📄</div>
            <p class="text-slate-500">Ajoutez des blocs pour voir l'aperçu</p>
          </div>
        </div>
      `;
    }

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <script src="https://cdn.tailwindcss.com"></script>
          <style>
            body { margin: 0; font-family: system-ui, -apple-system, sans-serif; }
            * { box-sizing: border-box; }
          </style>
        </head>
        <body>
          ${blocksHTML}
        </body>
      </html>
    `;
  };

  return (
    <div className="h-full bg-slate-100 flex items-center justify-center p-4">
      <div 
        className="bg-white shadow-2xl rounded-lg overflow-hidden transition-all duration-300"
        style={{ width: getPreviewWidth(), height: '100%', maxWidth: '100%' }}
      >
        <iframe
          ref={iframeRef}
          srcDoc={generatePreviewHTML()}
          className="w-full h-full border-0"
          title="Preview"
        />
      </div>
    </div>
  );
};

// Block Editor Component
const BlockEditor = ({ block, onUpdate, onDelete, onDuplicate, onMoveUp, onMoveDown, isFirst, isLast, isSelected, onSelect }) => {
  const [expanded, setExpanded] = useState(false);
  const blockType = BLOCK_TYPES.find(bt => bt.id === block.type);
  const Icon = blockType?.icon || Layout;

  const renderSettings = () => {
    switch (block.type) {
      case 'hero':
      case 'hero-video':
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Titre</Label>
              <Input value={block.content?.title || ''} onChange={(e) => onUpdate({ ...block, content: { ...block.content, title: e.target.value } })} placeholder="Titre principal" className="h-9" />
            </div>
            <div>
              <Label className="text-xs">Sous-titre</Label>
              <Textarea value={block.content?.subtitle || ''} onChange={(e) => onUpdate({ ...block, content: { ...block.content, subtitle: e.target.value } })} className="min-h-[60px]" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Bouton</Label>
                <Input value={block.content?.cta_text || ''} onChange={(e) => onUpdate({ ...block, content: { ...block.content, cta_text: e.target.value } })} className="h-9" />
              </div>
              <div>
                <Label className="text-xs">Lien</Label>
                <Input value={block.content?.cta_link || ''} onChange={(e) => onUpdate({ ...block, content: { ...block.content, cta_link: e.target.value } })} className="h-9" />
              </div>
            </div>
            <div>
              <Label className="text-xs">{block.type === 'hero-video' ? 'URL Vidéo' : 'Image de fond'}</Label>
              <Input value={block.content?.background || ''} onChange={(e) => onUpdate({ ...block, content: { ...block.content, background: e.target.value } })} className="h-9" />
            </div>
          </div>
        );
      case 'text':
        return (
          <div>
            <Label className="text-xs">Contenu</Label>
            <Textarea value={block.content?.text || ''} onChange={(e) => onUpdate({ ...block, content: { ...block.content, text: e.target.value } })} className="min-h-[120px]" />
          </div>
        );
      case 'image':
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">URL Image</Label>
              <Input value={block.content?.url || ''} onChange={(e) => onUpdate({ ...block, content: { ...block.content, url: e.target.value } })} className="h-9" />
            </div>
            {block.content?.url && <img src={block.content.url} alt="Preview" className="h-24 w-full object-cover rounded" />}
            <div>
              <Label className="text-xs">Texte alt (SEO)</Label>
              <Input value={block.content?.alt || ''} onChange={(e) => onUpdate({ ...block, content: { ...block.content, alt: e.target.value } })} className="h-9" />
            </div>
          </div>
        );
      case 'services':
      case 'features':
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Titre section</Label>
              <Input value={block.content?.title || ''} onChange={(e) => onUpdate({ ...block, content: { ...block.content, title: e.target.value } })} className="h-9" />
            </div>
            <div>
              <Label className="text-xs">Sous-titre</Label>
              <Textarea value={block.content?.subtitle || ''} onChange={(e) => onUpdate({ ...block, content: { ...block.content, subtitle: e.target.value } })} className="min-h-[60px]" />
            </div>
            <div>
              <Label className="text-xs">Colonnes</Label>
              <div className="flex gap-2 mt-1">
                {[2, 3, 4].map(n => (
                  <Button key={n} size="sm" variant={block.content?.columns === n ? 'default' : 'outline'} onClick={() => onUpdate({ ...block, content: { ...block.content, columns: n } })} className="h-8">
                    {n}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        );
      case 'stats':
        return (
          <div>
            <Label className="text-xs">Stats (valeur|label par ligne)</Label>
            <Textarea value={block.content?.stats_raw || '150+|Projets\n98%|Satisfaits'} onChange={(e) => onUpdate({ ...block, content: { ...block.content, stats_raw: e.target.value } })} className="min-h-[100px] font-mono text-xs" />
          </div>
        );
      case 'cta':
        return (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Titre</Label>
              <Input value={block.content?.title || ''} onChange={(e) => onUpdate({ ...block, content: { ...block.content, title: e.target.value } })} className="h-9" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea value={block.content?.description || ''} onChange={(e) => onUpdate({ ...block, content: { ...block.content, description: e.target.value } })} className="min-h-[60px]" />
            </div>
            <div>
              <Label className="text-xs">Texte bouton</Label>
              <Input value={block.content?.button_text || ''} onChange={(e) => onUpdate({ ...block, content: { ...block.content, button_text: e.target.value } })} className="h-9" />
            </div>
          </div>
        );
      case 'spacer':
        return (
          <div>
            <Label className="text-xs">Hauteur (px)</Label>
            <Input type="number" value={block.content?.height || 60} onChange={(e) => onUpdate({ ...block, content: { ...block.content, height: parseInt(e.target.value) } })} className="h-9" />
          </div>
        );
      default:
        return <p className="text-xs text-slate-500">Configuration à venir...</p>;
    }
  };

  return (
    <div className={cn(
      "border rounded-lg bg-white transition-all",
      isSelected ? "border-cyan-500 shadow-md" : "border-slate-200"
    )}>
      <div className="flex items-center gap-2 p-2 cursor-pointer hover:bg-slate-50" onClick={() => onSelect(block.id)}>
        <GripVertical className="w-4 h-4 text-slate-400 cursor-grab" />
        <div className={cn("w-7 h-7 rounded flex items-center justify-center", isSelected ? "bg-cyan-500" : "bg-slate-200")}>
          <Icon className={cn("w-3.5 h-3.5", isSelected ? "text-white" : "text-slate-600")} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-800 truncate">{blockType?.name}</p>
        </div>
        <div className="flex items-center gap-0.5">
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onMoveUp(); }} disabled={isFirst} className="h-6 w-6"><ChevronUp className="w-3 h-3" /></Button>
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onMoveDown(); }} disabled={isLast} className="h-6 w-6"><ChevronDown className="w-3 h-3" /></Button>
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onDuplicate(); }} className="h-6 w-6"><Copy className="w-3 h-3" /></Button>
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onDelete(); }} className="h-6 w-6 text-red-500"><Trash2 className="w-3 h-3" /></Button>
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }} className="h-6 w-6">
            <ChevronRight className={cn("w-3 h-3 transition-transform", expanded && "rotate-90")} />
          </Button>
        </div>
      </div>
      {expanded && <div className="p-3 pt-0 border-t">{renderSettings()}</div>}
    </div>
  );
};

// Main Page Builder
const PageBuilderPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [showNewPage, setShowNewPage] = useState(false);
  const [newPageTitle, setNewPageTitle] = useState('');
  const [activeTab, setActiveTab] = useState('blocks');
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [blockFilter, setBlockFilter] = useState('all');
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [seoGenerating, setSeoGenerating] = useState(false);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const res = await api.get('/website/pages');
      setPages(res.data);
      if (res.data.length > 0) setSelectedPage(res.data[0]);
    } catch (error) {
      const defaultPages = [
        { id: 'default-home', title: 'Accueil', slug: 'accueil', blocks: [], is_home: true, status: 'published' },
        { id: 'default-services', title: 'Services', slug: 'services', blocks: [], status: 'draft' }
      ];
      setPages(defaultPages);
      setSelectedPage(defaultPages[0]);
    } finally {
      setLoading(false);
    }
  };

  const saveVersion = () => {
    if (!selectedPage) return;
    const version = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      page: JSON.parse(JSON.stringify(selectedPage))
    };
    setVersions(prev => [version, ...prev].slice(0, 20));
  };

  const restoreVersion = (version) => {
    setSelectedPage(version.page);
    setShowVersions(false);
    toast.success('Version restaurée');
  };

  const savePage = async (status = selectedPage?.status) => {
    if (!selectedPage) return;
    setSaving(true);
    saveVersion();
    
    const pageToSave = { ...selectedPage, status };
    
    try {
      await api.put(`/website/pages/${selectedPage.id}`, pageToSave);
      setSelectedPage(pageToSave);
      toast.success(status === 'published' ? 'Page publiée !' : 'Brouillon sauvegardé');
    } catch (error) {
      setSelectedPage(pageToSave);
      toast.success('Sauvegardé');
    } finally {
      setSaving(false);
    }
  };

  const createNewPage = async () => {
    if (!newPageTitle.trim()) return;
    const newPage = {
      id: `page-${Date.now()}`,
      title: newPageTitle,
      slug: newPageTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      blocks: [],
      status: 'draft'
    };
    setPages([...pages, newPage]);
    setSelectedPage(newPage);
    setNewPageTitle('');
    setShowNewPage(false);
    toast.success('Page créée');
  };

  const addBlock = (blockType) => {
    if (!selectedPage) return;
    const newBlock = { id: `block-${Date.now()}`, type: blockType.id, content: {} };
    setSelectedPage({ ...selectedPage, blocks: [...(selectedPage.blocks || []), newBlock] });
    setSelectedBlockId(newBlock.id);
  };

  const updateBlock = (blockId, updated) => {
    if (!selectedPage) return;
    setSelectedPage({ ...selectedPage, blocks: selectedPage.blocks.map(b => b.id === blockId ? updated : b) });
  };

  const deleteBlock = (blockId) => {
    if (!selectedPage) return;
    setSelectedPage({ ...selectedPage, blocks: selectedPage.blocks.filter(b => b.id !== blockId) });
    if (selectedBlockId === blockId) setSelectedBlockId(null);
  };

  const duplicateBlock = (blockId) => {
    if (!selectedPage) return;
    const block = selectedPage.blocks.find(b => b.id === blockId);
    if (!block) return;
    const newBlock = { ...block, id: `block-${Date.now()}`, content: { ...block.content } };
    const idx = selectedPage.blocks.findIndex(b => b.id === blockId);
    const blocks = [...selectedPage.blocks];
    blocks.splice(idx + 1, 0, newBlock);
    setSelectedPage({ ...selectedPage, blocks });
  };

  const moveBlock = (blockId, dir) => {
    if (!selectedPage) return;
    const blocks = [...selectedPage.blocks];
    const idx = blocks.findIndex(b => b.id === blockId);
    if (dir === 'up' && idx > 0) [blocks[idx], blocks[idx-1]] = [blocks[idx-1], blocks[idx]];
    if (dir === 'down' && idx < blocks.length - 1) [blocks[idx], blocks[idx+1]] = [blocks[idx+1], blocks[idx]];
    setSelectedPage({ ...selectedPage, blocks });
  };

  const generateSEO = async () => {
    if (!selectedPage) return;
    setSeoGenerating(true);
    try {
      const res = await api.post('/website/seo/generate', { page_title: selectedPage.title, page_content: '', industry: 'creative agency' });
      setSelectedPage({ ...selectedPage, seo_title: res.data.title, seo_description: res.data.description, slug: res.data.slug });
      toast.success('SEO généré !');
    } catch (e) {
      setSelectedPage({ ...selectedPage, 
        seo_title: `${selectedPage.title} | MSC L'Agence`, 
        seo_description: `Découvrez ${selectedPage.title.toLowerCase()} par MSC L'Agence.`,
        slug: selectedPage.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')
      });
      toast.success('SEO généré');
    } finally {
      setSeoGenerating(false);
    }
  };

  const filteredBlocks = blockFilter === 'all' ? BLOCK_TYPES : BLOCK_TYPES.filter(b => b.category === blockFilter);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn("transition-all duration-300 pt-24", collapsed ? "md:ml-20" : "md:ml-64")}>
        {/* Top Toolbar */}
        <div className="h-12 bg-white border-b flex items-center justify-between px-4 sticky top-24 z-30">
          <div className="flex items-center gap-3">
            <select value={selectedPage?.id || ''} onChange={(e) => setSelectedPage(pages.find(p => p.id === e.target.value))} className="border rounded px-2 py-1 text-sm">
              {pages.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
            <Button variant="ghost" size="sm" onClick={() => setShowNewPage(true)}><Plus className="w-4 h-4" /></Button>
            <Badge variant={selectedPage?.status === 'published' ? 'default' : 'secondary'} className={selectedPage?.status === 'published' ? 'bg-green-500' : ''}>
              {selectedPage?.status === 'published' ? 'Publié' : 'Brouillon'}
            </Badge>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="flex bg-slate-100 rounded p-0.5">
              {[{ id: 'desktop', icon: Monitor }, { id: 'tablet', icon: Tablet }, { id: 'mobile', icon: Smartphone }].map(d => (
                <Button key={d.id} variant={previewDevice === d.id ? 'default' : 'ghost'} size="icon" className="h-7 w-7" onClick={() => setPreviewDevice(d.id)}>
                  <d.icon className="w-3.5 h-3.5" />
                </Button>
              ))}
            </div>
            <Button variant="ghost" size="sm" onClick={() => setShowVersions(true)}><History className="w-4 h-4 mr-1" />Versions</Button>
            <Button variant="outline" size="sm" onClick={() => window.open(`/site/${selectedPage?.slug || 'accueil'}`, '_blank')}>
              <ExternalLink className="w-4 h-4 mr-1" />Voir
            </Button>
            <Button variant="outline" size="sm" onClick={() => savePage('draft')} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Brouillon
            </Button>
            <Button size="sm" onClick={() => savePage('published')} disabled={saving} className="bg-green-500 hover:bg-green-600">
              <CheckCircle className="w-4 h-4 mr-1" />Publier
            </Button>
          </div>
        </div>

        {/* Split View */}
        <div className="flex h-[calc(100vh-8rem)]">
          {/* Left Panel - Editor */}
          <div className="w-[400px] bg-white border-r flex flex-col overflow-hidden">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full">
              <TabsList className="m-2 grid grid-cols-3">
                <TabsTrigger value="blocks" className="text-xs">Blocs</TabsTrigger>
                <TabsTrigger value="page" className="text-xs">Page</TabsTrigger>
                <TabsTrigger value="seo" className="text-xs">SEO</TabsTrigger>
              </TabsList>

              <TabsContent value="blocks" className="flex-1 overflow-hidden flex flex-col m-0">
                {/* Block Library */}
                <div className="p-2 border-b">
                  <div className="flex gap-1 flex-wrap">
                    {CATEGORIES.map(cat => (
                      <Badge key={cat.id} variant={blockFilter === cat.id ? 'default' : 'outline'} className="cursor-pointer text-xs" onClick={() => setBlockFilter(cat.id)}>
                        {cat.name}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="p-2 border-b max-h-40 overflow-y-auto">
                  <div className="grid grid-cols-3 gap-1">
                    {filteredBlocks.map(bt => (
                      <button key={bt.id} onClick={() => addBlock(bt)} className="p-2 border rounded hover:border-cyan-500 hover:bg-cyan-50 text-center transition-colors relative">
                        <bt.icon className="w-5 h-5 mx-auto mb-1 text-slate-600" />
                        <span className="text-xs text-slate-700 block truncate">{bt.name}</span>
                        {bt.pro && <Badge className="absolute -top-1 -right-1 text-[8px] bg-amber-500 px-1">PRO</Badge>}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Page Blocks */}
                <div className="flex-1 overflow-y-auto p-2 space-y-2">
                  <div className="text-xs font-medium text-slate-500 mb-2">Blocs de la page ({selectedPage?.blocks?.length || 0})</div>
                  {selectedPage?.blocks?.length > 0 ? (
                    selectedPage.blocks.map((block, idx) => (
                      <BlockEditor
                        key={block.id}
                        block={block}
                        onUpdate={(u) => updateBlock(block.id, u)}
                        onDelete={() => deleteBlock(block.id)}
                        onDuplicate={() => duplicateBlock(block.id)}
                        onMoveUp={() => moveBlock(block.id, 'up')}
                        onMoveDown={() => moveBlock(block.id, 'down')}
                        isFirst={idx === 0}
                        isLast={idx === selectedPage.blocks.length - 1}
                        isSelected={selectedBlockId === block.id}
                        onSelect={setSelectedBlockId}
                      />
                    ))
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      <Layout className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Cliquez sur un bloc ci-dessus</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="page" className="flex-1 overflow-y-auto p-4 m-0 space-y-4">
                <div>
                  <Label>Titre de la page</Label>
                  <Input value={selectedPage?.title || ''} onChange={(e) => setSelectedPage({ ...selectedPage, title: e.target.value })} />
                </div>
                <div>
                  <Label>URL (slug)</Label>
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-slate-500">/site/</span>
                    <Input value={selectedPage?.slug || ''} onChange={(e) => setSelectedPage({ ...selectedPage, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} className="flex-1" />
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={selectedPage?.is_home || false} onChange={(e) => setSelectedPage({ ...selectedPage, is_home: e.target.checked })} />
                  <span className="text-sm">Page d'accueil</span>
                </label>
                <div className="pt-4 border-t">
                  <Button variant="destructive" size="sm" onClick={() => { setPages(pages.filter(p => p.id !== selectedPage?.id)); setSelectedPage(pages[0]); }}>
                    <Trash2 className="w-4 h-4 mr-2" />Supprimer la page
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="seo" className="flex-1 overflow-y-auto p-4 m-0 space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Assistant SEO IA</Label>
                  <Button size="sm" onClick={generateSEO} disabled={seoGenerating} className="bg-purple-500 hover:bg-purple-600">
                    {seoGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />}
                    Générer
                  </Button>
                </div>
                <div>
                  <Label>Titre SEO</Label>
                  <Input value={selectedPage?.seo_title || ''} onChange={(e) => setSelectedPage({ ...selectedPage, seo_title: e.target.value })} placeholder="Titre pour les moteurs de recherche" />
                  <p className="text-xs text-slate-500 mt-1">{(selectedPage?.seo_title || '').length}/60</p>
                </div>
                <div>
                  <Label>Meta Description</Label>
                  <Textarea value={selectedPage?.seo_description || ''} onChange={(e) => setSelectedPage({ ...selectedPage, seo_description: e.target.value })} className="h-20" />
                  <p className="text-xs text-slate-500 mt-1">{(selectedPage?.seo_description || '').length}/160</p>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Panel - Live Preview */}
          <div className="flex-1 overflow-hidden">
            <LivePreview page={selectedPage} device={previewDevice} />
          </div>
        </div>
      </main>

      {/* New Page Dialog */}
      <Dialog open={showNewPage} onOpenChange={setShowNewPage}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nouvelle page</DialogTitle></DialogHeader>
          <div className="py-4">
            <Label>Titre</Label>
            <Input value={newPageTitle} onChange={(e) => setNewPageTitle(e.target.value)} placeholder="Ex: Nos réalisations" onKeyDown={(e) => e.key === 'Enter' && createNewPage()} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewPage(false)}>Annuler</Button>
            <Button onClick={createNewPage}>Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Versions Dialog */}
      <Dialog open={showVersions} onOpenChange={setShowVersions}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="w-5 h-5" />Historique des versions</DialogTitle></DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-2">
            {versions.length > 0 ? versions.map(v => (
              <div key={v.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm">{v.page.title}</p>
                  <p className="text-xs text-slate-500">{new Date(v.timestamp).toLocaleString('fr-FR')}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => restoreVersion(v)}>
                  <RotateCcw className="w-3 h-3 mr-1" />Restaurer
                </Button>
              </div>
            )) : (
              <p className="text-center text-slate-500 py-8">Aucune version sauvegardée</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PageBuilderPage;
